import { Tooltip } from 'components/common';
import {
  ComponentProps,
  Fragment,
  ReactNode,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { styled } from '@project44/manifest-ds';

const Text = styled('div', {
  fontSize: '$small',
  overflow: 'hidden',
  textAlign: 'left',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

interface TooltipContainerProps {
  children?: ReactNode;
}

interface TruncatedTextProps {
  children: string;
  css?: ComponentProps<typeof Text>['css'];
  tooltipOptions?: Partial<ComponentProps<typeof Tooltip>>;
  disableTooltip?: boolean;
}

const useEllipsisActive = (textRef: RefObject<HTMLDivElement>) => {
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);

  useEffect(() => {
    setIsEllipsisActive(
      textRef.current === null
        ? false
        : textRef.current.offsetWidth < textRef.current.scrollWidth,
    );
  }, [textRef]);

  return isEllipsisActive;
};

function TruncatedText({
  children,
  css,
  tooltipOptions = { disablePortal: false },
  disableTooltip,
}: TruncatedTextProps) {
  const textRef = useRef<HTMLDivElement>(null);
  const isEllipsisActive = useEllipsisActive(textRef);

  const TooltipContainer = useMemo(() => {
    return ({ children: ownChildren }: TooltipContainerProps) => (
      <Tooltip label={children} children={ownChildren} {...tooltipOptions} />
    );
  }, [children, tooltipOptions]);

  const Container =
    isEllipsisActive && !disableTooltip ? TooltipContainer : Fragment;

  return (
    <Container>
      <Text ref={textRef} css={css}>
        {children}
      </Text>
    </Container>
  );
}

export default TruncatedText;
